import { Injectable, signal, computed, Signal } from '@angular/core';
import { PoItem } from '../types-workflow';

@Injectable({
  providedIn: 'root'
})
export class PoItemSelectionService {
  // Store selected PO item IDs grouped by PO name
  private _selections = signal<Map<string, Set<string>>>(new Map());

  // Expose as readonly signal
  readonly selections = this._selections.asReadonly();

  /**
   * Get selected PO item IDs for a specific PO
   */
  getSelection(poName: string): Set<string> {
    return this._selections().get(poName) || new Set();
  }

  /**
   * Check if a PO item is selected
   */
  isSelected(poName: string, poItemId: string): boolean {
    const selection = this._selections().get(poName);
    return selection ? selection.has(poItemId) : false;
  }

  /**
   * Toggle selection for a PO item
   */
  toggle(poName: string, poItemId: string): void {
    this._selections.update(map => {
      const newMap = new Map(map);
      let selection = newMap.get(poName);

      if (!selection) {
        selection = new Set();
        newMap.set(poName, selection);
      } else {
        selection = new Set(selection); // Create a new Set to trigger reactivity
      }

      if (selection.has(poItemId)) {
        selection.delete(poItemId);
      } else {
        selection.add(poItemId);
      }

      newMap.set(poName, selection);
      return newMap;
    });
  }

  /**
   * Select a PO item
   */
  select(poName: string, poItemId: string): void {
    this._selections.update(map => {
      const newMap = new Map(map);
      let selection = newMap.get(poName);

      if (!selection) {
        selection = new Set();
      } else {
        selection = new Set(selection);
      }

      selection.add(poItemId);
      newMap.set(poName, selection);
      return newMap;
    });
  }

  /**
   * Deselect a PO item
   */
  deselect(poName: string, poItemId: string): void {
    this._selections.update(map => {
      const newMap = new Map(map);
      const selection = newMap.get(poName);

      if (selection) {
        const newSelection = new Set(selection);
        newSelection.delete(poItemId);
        newMap.set(poName, newSelection);
      }

      return newMap;
    });
  }

  /**
   * Select all PO items
   */
  selectAll(poName: string, poItemIds: string[]): void {
    this._selections.update(map => {
      const newMap = new Map(map);
      newMap.set(poName, new Set(poItemIds));
      return newMap;
    });
  }

  /**
   * Clear selection for a specific PO
   */
  clear(poName: string): void {
    this._selections.update(map => {
      const newMap = new Map(map);
      newMap.delete(poName);
      return newMap;
    });
  }

  /**
   * Clear all selections
   */
  clearAll(): void {
    this._selections.set(new Map());
  }

  /**
   * Get count of selected items for a PO
   */
  getSelectedCount(poName: string): number {
    return this.getSelection(poName).size;
  }

  /**
   * Get selected PO items (objects) from a list
   */
  getSelectedItems(poName: string, allItems: PoItem[]): PoItem[] {
    const selectedIds = this.getSelection(poName);
    return allItems.filter(item => selectedIds.has(item.po_item_id));
  }
}
